"use client";

import { startTransition, useEffect, useRef, useState, type ReactNode } from "react";
import Link from "next/link";
import TimingReportCTA from "@/components/TimingReportCTA";
import { getPanchang, type PanchangResponse } from "@/lib/api/panchang";
import type { CityData } from "@/lib/cities-data.types";
import TithiCityPicker from "./_components/TithiCityPicker";
import TithiIntentChooser from "./_components/TithiIntentChooser";
import type { DailyTimingTelemetry } from "./daily-timing-experiment";
import LiveTimingCountdown from "@/components/engine-visuals/LiveTimingCountdown";
import TithiTypesReference, {
  getTithiTypeInfo,
  getTithiTypeKey,
  type TithiTypeInfo,
} from "./_components/TithiTypesReference";
import {
  displayPakshaName,
  displayTithiName,
  displayVaraName,
  tithiDateLabel,
  TITHI_COPY,
  TITHI_DEFAULT_CITY,
  type TithiLanguage,
  type TithiPurposeId,
} from "./tithi-today.copy";

const CITY_STORAGE_KEY = "ish_city";

type PanchangTithi = PanchangResponse["tithi"];

export interface TithiTodayClientProps {
  showHeader?: boolean;
  language?: TithiLanguage;
  sourcePath?: "/tithi-today" | "/hi/tithi-today";
  initialDate: string;
  initialResult?: PanchangResponse | null;
  crossLinks: ReactNode;
  telemetry?: DailyTimingTelemetry;
}

function cityResultKey(city: CityData, date: string): string {
  return `${date}|${city.slug}|${city.latitude}|${city.longitude}`;
}

function isStoredCity(value: Partial<CityData>): value is CityData {
  return (
    typeof value.slug === "string" &&
    typeof value.name === "string" &&
    typeof value.state === "string" &&
    typeof value.latitude === "number" &&
    Number.isFinite(value.latitude) &&
    typeof value.longitude === "number" &&
    Number.isFinite(value.longitude) &&
    typeof value.timezone === "string"
  );
}

export default function TithiTodayClient({
  showHeader = true,
  language = "en",
  sourcePath = "/tithi-today",
  initialDate,
  initialResult = null,
  crossLinks,
  telemetry,
}: TithiTodayClientProps) {
  const {
    city,
    currentResult,
    error,
    selectedPurpose,
    setSelectedPurpose,
    handleCityChange,
    retry,
  } = useTithiTodayState(initialDate, initialResult);
  const copy = TITHI_COPY[language];
  const isLoading = !currentResult && !error;
  const surfaceRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (currentResult) telemetry?.answerRendered(currentResult, city.slug);
  }, [city.slug, currentResult, telemetry]);

  useEffect(() => {
    if (!selectedPurpose || !currentResult || error) return;
    const count = surfaceRef.current?.querySelectorAll('[data-primary-cta="true"]').length ?? 0;
    telemetry?.primaryCtaAudited(count, selectedPurpose);
  }, [currentResult, error, selectedPurpose, telemetry]);

  function handlePurposeChange(purpose: TithiPurposeId) {
    setSelectedPurpose(purpose);
    telemetry?.purposeSelected(purpose);
  }

  return (
    <div
      ref={surfaceRef}
      className="intent-interface-timing-first min-h-[calc(100vh-130px)]"
      data-daily-timing-surface="v2"
    >
      <div className="mx-auto max-w-2xl px-4 pb-10 pt-4 sm:px-6">
        {showHeader && (
          <TithiHeader
            language={language}
            date={initialDate}
            tithi={currentResult?.tithi}
            cityName={city.name}
          />
        )}
        <TithiCityPicker
          key={city.slug}
          city={city}
          language={language}
          onCityChange={handleCityChange}
        />
        {isLoading && (
          <p className="sr-only" aria-live="polite">
            {initialResult ? copy.refreshing : copy.loading}
          </p>
        )}
        <MainTithiCard
          language={language}
          isLoading={isLoading}
          isError={error}
          tithi={currentResult?.tithi}
          onRetry={retry}
        />
        <TithiPatternCard language={language} tithi={currentResult?.tithi} />
        <PanchangSnapshot language={language} result={currentResult} cityName={city.name} />
        <TithiIntentChooser
          language={language}
          cityName={city.name}
          result={currentResult}
          selectedPurpose={selectedPurpose}
          onPurposeChange={handlePurposeChange}
        />
        {selectedPurpose && currentResult && !error && (
          <div data-primary-cta="true">
            <TimingReportCTA language={language} sourcePath={sourcePath} />
          </div>
        )}
        <TithiTypesReference language={language} />
        {crossLinks}
      </div>
    </div>
  );
}

function useTithiTodayState(initialDate: string, initialResult: PanchangResponse | null) {
  const initialSnapshot = initialResult?.date === initialDate ? initialResult : null;
  const [city, setCity] = useState<CityData>(TITHI_DEFAULT_CITY);
  const initialKey = cityResultKey(TITHI_DEFAULT_CITY, initialDate);
  const [result, setResult] = useState<PanchangResponse | null>(initialSnapshot);
  const [resultKey, setResultKey] = useState<string | null>(initialSnapshot ? initialKey : null);
  const [error, setError] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [selectedPurpose, setSelectedPurpose] = useState<TithiPurposeId | null>(null);
  const currentKey = cityResultKey(city, initialDate);
  const currentResult = resultKey === currentKey ? (result ?? undefined) : undefined;

  useEffect(() => {
    const stored = loadStoredCity();
    if (stored && cityResultKey(stored, initialDate) !== initialKey) {
      startTransition(() => setCity(stored));
    }
  }, [initialDate, initialKey]);

  useEffect(() => {
    if (result && resultKey === currentKey) return;

    let cancelled = false;
    getPanchang({
      date: initialDate,
      latitude: city.latitude,
      longitude: city.longitude,
      tz: city.timezone,
    })
      .then((response) => {
        if (cancelled) return;
        setResult(response);
        setResultKey(currentKey);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });

    return () => {
      cancelled = true;
    };
  }, [
    city.latitude,
    city.longitude,
    city.timezone,
    currentKey,
    initialDate,
    reloadToken,
    result,
    resultKey,
  ]);

  function handleCityChange(nextCity: CityData) {
    setError(false);
    setCity(nextCity);
    storeCity(nextCity);
  }

  function retry() {
    setError(false);
    setReloadToken((value) => value + 1);
  }

  return {
    city,
    currentResult,
    error,
    selectedPurpose,
    setSelectedPurpose,
    handleCityChange,
    retry,
  };
}

function loadStoredCity(): CityData | null {
  try {
    const raw = localStorage.getItem(CITY_STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as Partial<CityData>;
    return isStoredCity(stored) ? stored : null;
  } catch {
    return null;
  }
}

function storeCity(city: CityData): void {
  try {
    localStorage.setItem(CITY_STORAGE_KEY, JSON.stringify(city));
  } catch {
    // Browser storage is optional; the selected city still works for this visit.
  }
}

function TithiHeader({
  language,
  date,
  tithi,
  cityName,
}: {
  language: TithiLanguage;
  date: string;
  tithi?: PanchangTithi;
  cityName: string;
}) {
  const copy = TITHI_COPY[language];
  const isShukla = tithi?.paksha.toLowerCase().includes("shukla");
  return (
    <div className="mb-8 text-center">
      <div className="mb-3 text-5xl">{isShukla ? "🌕" : "🌑"}</div>
      <h1 className="mb-2 font-serif text-2xl font-bold text-ish-dark sm:text-3xl">
        {copy.headerTitle}
      </h1>
      <p className="text-body-small text-ish-text-muted">
        {copy.headerSubtitle} · {tithiDateLabel(date, language)} · {cityName}
      </p>
    </div>
  );
}

function MainTithiCard({
  language,
  isLoading,
  isError,
  tithi,
  onRetry,
}: {
  language: TithiLanguage;
  isLoading: boolean;
  isError: boolean;
  tithi?: PanchangTithi;
  onRetry: () => void;
}) {
  const copy = TITHI_COPY[language];
  const typeInfo = tithi ? getTithiTypeInfo(tithi.number, language) : null;

  return (
    <div className="card-sacred mb-5 min-h-[260px] p-6 text-center">
      {isLoading && <TithiLoading language={language} />}
      {isError && !isLoading && (
        <div className="py-8">
          <p className="text-body-small text-error">{copy.error}</p>
          <button
            onClick={onRetry}
            className="mt-3 text-body-small font-semibold text-intent-brand"
          >
            {copy.retry}
          </button>
        </div>
      )}
      {!isLoading && !isError && tithi && typeInfo && (
        <TithiDetails language={language} tithi={tithi} typeInfo={typeInfo} />
      )}
    </div>
  );
}

function TithiLoading({ language }: { language: TithiLanguage }) {
  return (
    <div className="py-8">
      <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-ish-accent border-t-transparent" />
      <p className="text-body-small text-ish-text-faint">{TITHI_COPY[language].loading}</p>
    </div>
  );
}

function TithiDetails({
  language,
  tithi,
  typeInfo,
}: {
  language: TithiLanguage;
  tithi: PanchangTithi;
  typeInfo: TithiTypeInfo;
}) {
  const tithiName = displayTithiName(tithi.name, language);
  return (
    <>
      <p className="mb-2 text-eyebrow font-semibold uppercase tracking-eyebrow text-intent-brand">
        {displayPakshaName(tithi.paksha, language)}
      </p>
      <h2 className="mb-1 font-serif text-3xl font-bold text-ish-dark sm:text-4xl">{tithiName}</h2>
      {language === "hi" && tithiName !== tithi.name && (
        <p className="mb-4 text-body-small text-ish-text-muted">{tithi.name}</p>
      )}
      <TithiNumberDots tithi={tithi} />
      <div className="mb-4 flex flex-wrap items-center justify-center gap-2">
        <div
          className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-body-small font-semibold ${typeInfo.badgeClass}`}
        >
          {tithi.name === "Purnima" ? "🌕" : tithi.name === "Amavasya" ? "🌑" : "◉"}{" "}
          {typeInfo.label}
        </div>
        {tithi.ends_at && (
          <LiveTimingCountdown
            labelEn="Ends in"
            labelHi="तिथि समाप्ति"
            endsAt={tithi.ends_at}
            // Rikta is the one inauspicious tithi type. Read the typed key, not
            // the localized display label — that copy changes per language.
            isAuspicious={getTithiTypeKey(tithi.number) !== "Rikta"}
            locale={language}
          />
        )}
      </div>
    </>
  );
}

function TithiNumberDots({ tithi }: { tithi: PanchangTithi }) {
  const visibleNumber = tithi.number > 15 ? tithi.number - 15 : tithi.number;
  return (
    <div className="mb-5 flex items-center justify-center gap-3" aria-hidden="true">
      {Array.from({ length: 15 }, (_, index) => {
        const active = index + 1 === visibleNumber;
        return (
          <span
            key={index}
            className="h-2 w-2 rounded-full transition"
            style={{
              backgroundColor: active ? "var(--ish-accent)" : "var(--ish-border)",
              transform: active ? "scale(1.5)" : "scale(1)",
            }}
          />
        );
      })}
    </div>
  );
}

function TithiPatternCard({ language, tithi }: { language: TithiLanguage; tithi?: PanchangTithi }) {
  if (!tithi) return <TithiCardReserve minHeight={156} />;
  const info = getTithiTypeInfo(tithi.number, language);
  return (
    <div className="card-sacred mb-5 min-h-[156px] p-5">
      <h3 className="mb-3 text-body font-bold text-ish-dark">
        {TITHI_COPY[language].patternTitle}
      </h3>
      <p className="text-body-small font-semibold text-ish-dark">{info.label}</p>
      <p className="mt-2 text-body-small leading-relaxed text-ish-text-muted">{info.desc}</p>
    </div>
  );
}

function PanchangSnapshot({
  language,
  result,
  cityName,
}: {
  language: TithiLanguage;
  result?: PanchangResponse;
  cityName: string;
}) {
  if (!result) return <TithiCardReserve minHeight={244} />;
  const copy = TITHI_COPY[language];
  const items = [
    {
      label: copy.tithi,
      value: `${displayTithiName(result.tithi.name, language)} (${displayPakshaName(
        result.tithi.paksha,
        language
      )})`,
    },
    { label: copy.nakshatra, value: result.nakshatra.name },
    { label: copy.yoga, value: result.yoga.name },
    { label: copy.karana, value: result.karana.name },
    { label: copy.vara, value: displayVaraName(result.vara, language) },
    { label: copy.sunrise, value: result.sunrise },
  ];

  return (
    <div className="card-sacred mb-5 min-h-[244px] p-5">
      <h3 className="mb-4 text-body font-bold text-ish-dark">
        {copy.snapshotTitle} · {cityName}
      </h3>
      <div className="grid grid-cols-2 gap-3">
        {items.map((item) => (
          <div key={item.label} className="rounded-lg bg-surface-warm p-3">
            <p className="mb-1 text-eyebrow uppercase tracking-eyebrow text-ish-text-faint">
              {item.label}
            </p>
            <p className="text-body-small font-semibold text-ish-dark">{item.value}</p>
          </div>
        ))}
      </div>
      <div className="mt-3 text-center">
        <Link
          href={language === "hi" ? "/hi/panchang/" : "/panchang/"}
          className="text-body-small font-semibold text-intent-brand"
        >
          {copy.fullPanchang} →
        </Link>
      </div>
    </div>
  );
}

function TithiCardReserve({ minHeight }: { minHeight: number }) {
  return (
    <div aria-hidden="true" className="card-sacred mb-5 p-5" style={{ minHeight }}>
      <div className="h-4 w-36 rounded bg-surface-warm" />
      <div className="mt-4 space-y-2">
        <div className="h-3 w-full rounded bg-surface-warm" />
        <div className="h-3 w-5/6 rounded bg-surface-warm" />
        <div className="h-3 w-2/3 rounded bg-surface-warm" />
      </div>
    </div>
  );
}
